"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildDonationThankYouMockData = exports.FROALA_DONATION_FIGMA_THANK_YOU_BODY = void 0;
const luxon_1 = require("luxon");
const enums_1 = require("../../enums");
const email_theme_tokens_1 = require("../../helpers/email-theme-tokens");
const recurrence_interval_1 = require("../../helpers/recurrence-interval");
const helpers_1 = require("../helpers");
const subscription_charge_day_1 = require("../../helpers/subscription-charge-day");
const email_templates_1 = require("../../internationalization/locales/email-templates");
const subscription_1 = require("../../types/subscription");
const icons_1 = require("../icons");
const format_mock_currency_1 = require("./format-mock-currency");
const organization_page_button_1 = require("./organization-page-button");
const MOCK_RECEIPT_URL = "https://example.com/receipt/abc123";
const MOCK_CONNECTION_LINK = "https://zeffy.com/my-account";
const MOCK_MANAGE_RECURRING_DONATION_URL = "https://www.zeffy.com/en-US/manage-recurring-donation#token=sandbox-token";
const SUBSCRIPTION_INTERVAL = {
    [enums_1.ProductDonationRecurrenceInterval.OneTime]: null,
    [enums_1.ProductDonationRecurrenceInterval.Weekly]: subscription_1.SubscriptionRecurrenceInterval.Weekly,
    [enums_1.ProductDonationRecurrenceInterval.BiWeekly]: subscription_1.SubscriptionRecurrenceInterval.BiWeekly,
    [enums_1.ProductDonationRecurrenceInterval.Monthly]: subscription_1.SubscriptionRecurrenceInterval.Monthly,
    [enums_1.ProductDonationRecurrenceInterval.Quarterly]: subscription_1.SubscriptionRecurrenceInterval.Quarterly,
    [enums_1.ProductDonationRecurrenceInterval.Yearly]: subscription_1.SubscriptionRecurrenceInterval.Yearly,
};
const MOCK_DONOR_REFUND_LINK = "https://zeffy.com/tip-adjustment";
const FROALA_PARAGRAPH_STYLE = "width:100%;margin:0px;box-sizing:inherit;color:rgb(0, 0, 0);line-height:1.5;";
exports.FROALA_DONATION_FIGMA_THANK_YOU_BODY = `<p style="${FROALA_PARAGRAPH_STYLE}">Hi John,</p>
<p style="${FROALA_PARAGRAPH_STYLE}">Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.</p>
<p style="${FROALA_PARAGRAPH_STYLE}">Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.</p>
<p style="${FROALA_PARAGRAPH_STYLE}">Warm regards,</p>
<p style="${FROALA_PARAGRAPH_STYLE}"><strong>The Cool Foundation Team</strong></p>`;
const buildDonationThankYouMockData = ({ orgName, color, isDarkMode, body, recurrenceInterval = enums_1.ProductDonationRecurrenceInterval.OneTime, firstName = "John", lastName = "Smith", donorAddress = "San Diego, CA", companyName, paymentMethodDisplay = "Card (••• 4242)", currentDate, donationAmountCents = 2000, proratedFromAmountCents, contributionCents = 100, isOfflinePayment = false, isCashPayment = false, isChequePending = false, isChequeReceived = false, chequeInstructions, chequeReceiptPromised = true, shouldHideAmounts = false, receiptUrl = MOCK_RECEIPT_URL, hasReceipt = true, hasGiftAidConfirmation = false, giftaidExtraAmount, charityNumber, transactionReceiptCharityNumberLabel, registeredCharityName, registeredCharityNumber, locale = "en", country, tribute, tributeName, organizationPageUrl, }) => {
    const formatAmount = (cents) => (0, format_mock_currency_1.formatMockCurrencyAmount)(cents, { locale, country });
    const labels = (0, email_templates_1.getEmailLabels)(locale);
    const theme = (0, email_theme_tokens_1.resolveEmailThemeTokens)(color || "#FFFFFF", isDarkMode ? "dark" : "light");
    const iconZeffyLogo = theme.zeffyLogoVariant === "dark" ? icons_1.EMAIL_ICON_DATA_URI.zeffyDark : icons_1.EMAIL_ICON_DATA_URI.zeffyLight;
    const iconGiftAid = theme.zeffyLogoVariant === "dark" ? icons_1.EMAIL_ICON_DATA_URI.giftAidDark : icons_1.EMAIL_ICON_DATA_URI.giftAidLight;
    const hasTribute = Boolean(tribute);
    const isOneTime = recurrenceInterval === enums_1.ProductDonationRecurrenceInterval.OneTime;
    const isWeekly = recurrenceInterval === enums_1.ProductDonationRecurrenceInterval.Weekly;
    const isBiWeekly = recurrenceInterval === enums_1.ProductDonationRecurrenceInterval.BiWeekly;
    const isMonthly = recurrenceInterval === enums_1.ProductDonationRecurrenceInterval.Monthly;
    const isQuarterly = recurrenceInterval === enums_1.ProductDonationRecurrenceInterval.Quarterly;
    const isYearly = recurrenceInterval === enums_1.ProductDonationRecurrenceInterval.Yearly;
    const hasContribution = contributionCents > 0;
    const isChequeDonation = isChequePending || isChequeReceived;
    const shouldDisplayContributionLine = !isOfflinePayment && !isChequeDonation && !shouldHideAmounts;
    const paidCents = donationAmountCents + contributionCents;
    const anchorDate = currentDate ?? luxon_1.DateTime.now();
    const hasRenewalBlock = isWeekly || isBiWeekly || isMonthly;
    const subscriptionInterval = SUBSCRIPTION_INTERVAL[recurrenceInterval];
    const renewalPeriod = hasRenewalBlock && subscriptionInterval ? subscription_charge_day_1.SUBSCRIPTION_PERIOD[subscriptionInterval] : null;
    const cumulativeReceiptYear = String(anchorDate.year + 1);
    return {
        ...(0, organization_page_button_1.buildOrganizationPageButtonMockFields)(organizationPageUrl, theme),
        labels,
        lang: locale,
        cumulativeReceiptYear,
        theme,
        iconZeffyLogo,
        iconGiftAid,
        isDonation: true,
        subject: "Thank you for your donation!",
        orgName: orgName || "Bright Futures Foundation",
        firstName,
        lastName,
        address: donorAddress,
        companyName: companyName ?? "",
        date: (currentDate ?? luxon_1.DateTime.now()).toFormat("LLLL d, yyyy"),
        paymentMethodDisplay,
        simplykConnectionLink: MOCK_CONNECTION_LINK,
        message: body,
        isOneTime,
        isWeekly,
        isBiWeekly,
        isMonthly,
        isQuarterly,
        isYearly,
        isRecurring: !isOneTime,
        showsRenewalScheduleSentence: !isOneTime && !hasRenewalBlock,
        ...(!isOneTime && !hasRenewalBlock ? (0, helpers_1.buildRecurringChargeFields)(anchorDate, recurrenceInterval, locale) : {}),
        nextRenewalDate: renewalPeriod
            ? anchorDate.plus(renewalPeriod).setLocale(locale).toLocaleString(luxon_1.DateTime.DATE_FULL)
            : "",
        nextRenewalAmount: formatAmount(paidCents),
        manageRecurringDonationUrl: hasRenewalBlock ? MOCK_MANAGE_RECURRING_DONATION_URL : "",
        hasCumulativeReceipt: (0, recurrence_interval_1.hasCumulativeAnnualReceipt)(recurrenceInterval),
        donationAmount: formatAmount(donationAmountCents),
        proratedFromAmount: proratedFromAmountCents ? formatAmount(proratedFromAmountCents) : null,
        contributionAmount: formatAmount(contributionCents),
        paidAmount: formatAmount(paidCents),
        shouldHideAmounts,
        hasContribution,
        shouldDisplayContributionLine,
        isOfflinePayment,
        isCashPayment,
        isChequePending,
        isChequeReceived,
        chequeInstructions: chequeInstructions ?? "",
        chequeReceiptPromised,
        shouldDisplayDonorRefundOptionInEmail: hasContribution && !isOfflinePayment,
        donorRefundPageLink: MOCK_DONOR_REFUND_LINK,
        hasReceipt,
        receiptUrl: hasReceipt ? receiptUrl : "",
        hasGiftAidConfirmation,
        hasGiftAid: hasGiftAidConfirmation ? "Yes" : "No",
        giftaidExtraAmount: giftaidExtraAmount ?? "",
        shouldDisplayCharityNumberOnTransactionReceipt: Boolean(charityNumber),
        charityNumber: charityNumber ?? "",
        transactionReceiptCharityNumberLabel: transactionReceiptCharityNumberLabel ?? "",
        registeredCharityName: registeredCharityName ?? "",
        registeredCharityNumber: registeredCharityNumber ?? "",
        hasTribute,
        isInHonour: tribute === enums_1.Tribute.InHonourOf,
        isInMemory: tribute === enums_1.Tribute.InMemoryOf,
        tributeName: tributeName ?? "",
        iconHeart: hasTribute ? (0, icons_1.tintIconSvgDataUri)("heart", theme.tBrand) : "",
    };
};
exports.buildDonationThankYouMockData = buildDonationThankYouMockData;
